<template>
  <div class="institutional-page-sync-button">
    <!-- Botão Discreto de Sincronização -->
    <button
      v-if="isAdminMode"
      @click="toggleSyncPanel"
      class="sync-button"
      :title="syncStatus === 'syncing' ? 'Sincronizando...' : 'Sincronizar páginas institucionais'"
      :disabled="syncStatus === 'syncing'"
    >
      <i :class="['fas', syncStatus === 'syncing' ? 'fa-spinner fa-spin' : 'fa-sync-alt']"></i>
      <span class="sync-label">{{ syncStatus === 'syncing' ? 'Sincronizando' : 'Sincronizar' }}</span>
    </button>

    <!-- Painel de Sincronização -->
    <transition name="slide-fade">
      <div v-if="showSyncPanel" class="sync-panel">
        <div class="sync-panel-header">
          <h4>Sincronizar Páginas Institucionais</h4>
          <button @click="toggleSyncPanel" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="sync-panel-content">
          <!-- Status de Sincronização -->
          <div class="sync-status">
            <div v-if="syncStatus === 'idle'" class="status-idle">
              <i class="fas fa-info-circle"></i>
              <p>Clique em "Sincronizar Agora" para atualizar as páginas institucionais com as alterações do gerenciador.</p>
            </div>

            <div v-if="syncStatus === 'syncing'" class="status-syncing">
              <i class="fas fa-spinner fa-spin"></i>
              <p>Sincronizando páginas...</p>
            </div>

            <div v-if="syncStatus === 'success'" class="status-success">
              <i class="fas fa-check-circle"></i>
              <p>Páginas sincronizadas com sucesso!</p>
              <small v-if="lastSync">Última sincronização: {{ formatDate(lastSync) }}</small>
            </div>

            <div v-if="syncStatus === 'error'" class="status-error">
              <i class="fas fa-exclamation-circle"></i>
              <p>Erro ao sincronizar páginas.</p>
              <small>{{ errorMessage }}</small>
            </div>
          </div>

          <!-- Informações de Páginas -->
          <div v-if="pageCount > 0" class="pages-info">
            <p><strong>Total de páginas:</strong> {{ pageCount }}</p>
          </div>

          <!-- Botões de Ação -->
          <div class="sync-actions">
            <button
              @click="syncPages"
              class="btn-sync-now"
              :disabled="syncStatus === 'syncing'"
            >
              <i class="fas fa-sync-alt"></i> Sincronizar Agora
            </button>
            <button @click="toggleSyncPanel" class="btn-close">
              <i class="fas fa-times"></i> Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'InstitutionalPageSyncButton',
  data() {
    return {
      isAdminMode: false,
      showSyncPanel: false,
      syncStatus: 'idle', // idle, syncing, success, error
      lastSync: null,
      pageCount: 0,
      errorMessage: ''
    };
  },
  mounted() {
    // Verificar se está em modo admin (pode ser verificado via localStorage ou query param)
    this.checkAdminMode();
    this.checkLastSync();
  },
  methods: {
    checkAdminMode() {
      // Verificar se há um token de admin ou flag no localStorage
      const adminToken = localStorage.getItem('admin_token');
      const adminMode = localStorage.getItem('admin_mode');
      this.isAdminMode = !!adminToken || adminMode === 'true' || this.$route.query.admin === 'true';
    },
    toggleSyncPanel() {
      this.showSyncPanel = !this.showSyncPanel;
    },
    async checkLastSync() {
      try {
        const response = await fetch('/api/sync_institutional_pages.php?action=check_sync');
        const data = await response.json();
        
        if (data.status === 'success') {
          this.lastSync = data.lastSync;
          this.pageCount = data.pageCount;
        }
      } catch (error) {
        console.error('Erro ao verificar sincronização:', error);
      }
    },
    async syncPages() {
      this.syncStatus = 'syncing';
      this.errorMessage = '';

      try {
        // Primeiro, verificar se as páginas estão disponíveis
        const response = await fetch('/api/sync_institutional_pages.php?action=get_all');
        const data = await response.json();

        if (data.status === 'success') {
          // Armazenar páginas localmente para uso no componente
          localStorage.setItem('institutional_pages', JSON.stringify(data.pages));
          localStorage.setItem('pages_last_sync', new Date().toISOString());

          this.syncStatus = 'success';
          this.lastSync = new Date().toISOString();
          this.pageCount = data.pages.length;

          // Emitir evento para notificar outros componentes
          this.$root.$emit('pages-synced', data.pages);

          // Resetar status após 3 segundos
          setTimeout(() => {
            this.syncStatus = 'idle';
          }, 3000);
        } else {
          throw new Error(data.message || 'Erro ao sincronizar');
        }
      } catch (error) {
        console.error('Erro ao sincronizar páginas:', error);
        this.syncStatus = 'error';
        this.errorMessage = error.message || 'Erro ao sincronizar páginas';

        // Resetar status após 5 segundos
        setTimeout(() => {
          this.syncStatus = 'idle';
        }, 5000);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    }
  }
};
</script>

<style scoped lang="scss">
.institutional-page-sync-button {
  position: relative;
  z-index: 100;
}

.sync-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #2c3951 0%, #a8551e 40%, #a84605 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(168, 70, 5, 0.3);
  transition: all 0.3s ease;
  font-size: 0.9rem;

  i {
    font-size: 1rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(168, 70, 5, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .sync-label {
    @media (max-width: 768px) {
      display: none;
    }
  }
}

.sync-panel {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 320px;
  max-width: 90vw;
  z-index: 99;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100vw - 40px);
    bottom: 80px;
    right: 20px;
  }
}

.sync-panel-header {
  background: linear-gradient(135deg, #2c3951 0%, #a8551e 40%, #a84605 100%);
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1.2rem;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.8;
    }
  }
}

.sync-panel-content {
  padding: 20px;
}

.sync-status {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;

  i {
    font-size: 2rem;
    margin-bottom: 10px;
    display: block;
  }

  p {
    margin: 10px 0 0 0;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  small {
    display: block;
    font-size: 0.8rem;
    margin-top: 8px;
  }
}

.status-idle {
  background: #fdede3;
  color: #a1480d;

  i {
    color: #a1480d;
  }
}

.status-syncing {
  background: #fff3e0;
  color: #e65100;

  i {
    color: #e65100;
    animation: spin 1s linear infinite;
  }
}

.status-success {
  background: #e8f5e9;
  color: #2e7d32;

  i {
    color: #2e7d32;
  }
}

.status-error {
  background: #ffebee;
  color: #c62828;

  i {
    color: #c62828;
  }
}

.pages-info {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 0.9rem;

  p {
    margin: 0;
  }
}

.sync-actions {
  display: flex;
  gap: 10px;

  button {
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    i {
      font-size: 0.9rem;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-sync-now {
    background: linear-gradient(135deg, #2e7d32 0%, #43a047 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
    }
  }

  .btn-close {
    background: #e0e0e0;
    color: #424242;

    &:hover {
      background: #d0d0d0;
    }
  }
}

// Animações
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
